import type { DoseUnit, FormularyKind, InventoryItem, InventoryItemDetail, InventoryLot, InventoryShelf, InvoiceDraft, InvoiceMatch, PurchaseAttachment, Supplier } from "@vaulted/shared";
import { CHANGE_REASON_LABELS, DOSE_UNITS, DOSE_UNIT_LABELS, amountFor, formatDose, formatVials, needsStrength, perVial, vialsOf } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";
import { Link, Navigate, useParams } from "react-router";
import { errorMessage, get, post, put } from "../api";
import { InvoiceUpload } from "../components/InvoiceUpload";
import { Alert, Badge, Button, Checkbox, Dialog, Field, Loading, MoneyField, PageHeader, Panel, SelectField, Tabs, TextAreaField, cx, money } from "../components/ui";
import { todayIn } from "./Calendar";
import { PurchasesList } from "./Purchases";
import { useSession } from "../session";
import type { CatalogResponse } from "./Catalog";

const TABS: { key: string; kind: FormularyKind; label: string }[] = [
  { key: "medications", kind: "medication", label: "Medications" },
  { key: "fluids", kind: "fluid", label: "Fluids" },
  { key: "supplies", kind: "supply", label: "Supplies" },
];

const fmtNum = (n: number, max = 2) => new Intl.NumberFormat("en-US", { maximumFractionDigits: max }).format(n);
const fmtDate = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });

/** Inventory: medications on shelves (vials, alphabetical), fluids, and supplies, by lot. */
export function InventoryPage() {
  const { tab } = useParams();
  const { can } = useSession();
  const manage = can("inventory.manage");
  const shelf = useQuery({ queryKey: ["inventory"], queryFn: () => get<InventoryShelf>("/api/inventory") });
  const [open, setOpen] = useState<string | null>(null);
  const [receiving, setReceiving] = useState(false);
  const current = TABS.find((t) => t.key === tab);
  const purchases = tab === "purchases" && manage;
  if (!current && !purchases) return <Navigate to="/inventory/medications" replace />;
  const items = current ? (shelf.data?.items.filter((i) => i.kind === current.kind) ?? []) : [];

  return (
    <div>
      <PageHeader
        title="Inventory"
        description="What's on hand, by lot. Signed charts take what was given off automatically."
        action={
          manage && (
            <Button variant="primary" onClick={() => setReceiving(true)}>
              Record purchase
            </Button>
          )
        }
      />
      <Tabs items={[...TABS.map((t) => ({ to: `/inventory/${t.key}`, label: t.label })), ...(manage ? [{ to: "/inventory/purchases", label: "Purchases" }] : [])]} />
      {shelf.error && <Alert>{errorMessage(shelf.error)}</Alert>}
      {purchases ? (
        <PurchasesList />
      ) : !shelf.data || !current ? (
        !shelf.error && <Loading />
      ) : items.length === 0 ? (
        <p className="text-sm text-muted">
          Nothing here yet. Add {current.label.toLowerCase()} in <Link to="/catalog/formulary" className="font-semibold text-brand">Catalog → Formulary</Link>
          {manage ? ", or record a purchase." : "."}
        </p>
      ) : current.kind === "supply" ? (
        <SupplyList items={items} onOpen={setOpen} />
      ) : (
        <Shelves items={items} onOpen={setOpen} />
      )}
      {open && <ItemDialog id={open} onClose={() => setOpen(null)} />}
      {receiving && <RecordPurchaseDialog kind={current?.kind ?? "medication"} onClose={() => setReceiving(false)} />}
    </div>
  );
}

// ── Shelves


function Shelves({ items, onOpen }: { items: InventoryItem[]; onOpen: (id: string) => void }) {
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(11rem,1fr))] gap-y-6">
      {items.map((it) => (
        <button key={it.formularyItemId} type="button" onClick={() => onOpen(it.formularyItemId)} className="group flex flex-col text-left">
          <div className="flex h-24 items-end gap-1 overflow-hidden px-2.5 pt-2">
            <VialRow item={it} />
          </div>
          {/* the shelf */}
          <div className="h-2 rounded-sm bg-[#8A6A4F] shadow-[0_3px_0_#5E4634]" aria-hidden />
          <div className="px-3 pt-2.5">
            <div className="truncate font-semibold group-hover:text-brand">{it.name}</div>
            {strengthOf(it) && <div className="truncate text-xs text-muted">{strengthOf(it)}</div>}
            <div className="text-sm text-ink-soft tabular-nums">
              {it.lots.length === 0 ? <span className="text-muted">None on hand</span> : <>{summary(it)}</>}
            </div>
            <ItemFlags item={it} />
          </div>
        </button>
      ))}
    </div>
  );
}

/** "300 mg/mL · 30 mL vial", from the lot in front (else the usual): what's in each vial. */
function strengthOf(it: InventoryItem) {
  const front = it.lots.find((l) => l.remaining > 0 && l.status !== "expired") ?? it.lots[0];
  const strength = front ? front.strengthPerMl : it.strengthPerMl;
  const vial = front ? front.vialSizeMl : it.vialSizeMl;
  if (it.doseUnit === "each") return "";
  const parts = [strength !== null && it.doseUnit !== "ml" ? `${fmtNum(strength, 4)} ${DOSE_UNIT_LABELS[it.doseUnit]}/mL` : "", vial !== null ? `${fmtNum(vial)} mL ${it.kind === "fluid" ? "bag" : "vial"}` : ""];
  return parts.filter(Boolean).join(" · ");
}

function summary(it: InventoryItem) {
  const lot = it.lots.find((l) => l.status !== "expired") ?? it.lots[0];
  const vials = formatVials(it.usable, lot?.perVial ?? null, it.doseUnit, it.kind);
  return it.doseUnit === "each" ? vials : `${vials} · ${formatDose(it.usable, it.doseUnit)}`;
}

function ItemFlags({ item: it }: { item: InventoryItem }) {
  const expiring = it.lots.some((l) => l.status === "expiring" && l.remaining > 0);
  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {it.low && <span className="inline-flex rounded-full bg-danger/10 px-2.5 py-0.5 text-xs font-semibold text-danger">Low stock</span>}
      {it.countOff && <Badge tone="wait">Count off: check</Badge>}
      {it.expired > 0 && <span className="inline-flex rounded-full bg-danger/10 px-2.5 py-0.5 text-xs font-semibold text-danger">Expired on shelf</span>}
      {expiring && <Badge tone="wait">Expiring soon</Badge>}
      {it.needsDetails && <Badge tone="off">Check lot / BUD</Badge>}
    </div>
  );
}

/** Vials for each lot, soonest BUD at the front (left); the open vial filled to its level. */
/** Room on a shelf slot for vials (px), before "+N". */
const SHELF_ROOM = 150;

function VialRow({ item: it }: { item: InventoryItem }) {
  const vials: { key: string; fill: number; status: InventoryLot["status"]; ml: number | null }[] = [];
  for (const l of it.lots) {
    if (l.remaining <= 0) continue;
    const v = vialsOf(l.remaining, l.perVial);
    if (!v) {
      vials.push({ key: `${l.id}-?`, fill: 1, status: l.status, ml: l.vialSizeMl });
      continue;
    }
    // The open (partial) vial goes first: it's the one in use.
    if (v.partial > 0) vials.push({ key: `${l.id}-p`, fill: v.partial, status: l.status, ml: l.vialSizeMl });
    for (let i = 0; i < v.full; i++) vials.push({ key: `${l.id}-${i}`, fill: 1, status: l.status, ml: l.vialSizeMl });
  }
  if (!vials.length) return <span className="pb-1 text-xs text-muted">Empty</span>;
  const bag = it.kind === "fluid";
  // As many as fit on the slot, leaving room for "+N".
  const shown: typeof vials = [];
  let used = 0;
  for (const v of vials) {
    const w = (bag ? 18 : vialDims(v.ml).w) + 3;
    if (used + w > SHELF_ROOM - (vials.length > shown.length + 1 ? 22 : 0)) break;
    shown.push(v);
    used += w;
  }
  return (
    <>
      {shown.map((v) => (bag ? <Bag key={v.key} color={it.color} fill={v.fill} status={v.status} /> : <Vial key={v.key} color={it.color} fill={v.fill} status={v.status} ml={v.ml} />))}
      {vials.length > shown.length && <span className="pb-1 pl-1 text-xs font-semibold text-ink-soft">+{vials.length - shown.length}</span>}
    </>
  );
}

/** Drawn size by vial size, after real vials: 2 mL is small; 30 and 50 mL share the wide multi-dose shape. */
function vialDims(ml: number | null): { w: number; h: number } {
  if (ml === null) return { w: 30, h: 66 };
  if (ml <= 2) return { w: 14, h: 30 };
  if (ml <= 5) return { w: 19, h: 36 };
  if (ml <= 10) return { w: 19, h: 44 };
  if (ml <= 20) return { w: 23, h: 52 };
  if (ml <= 60) return { w: 30, h: 66 };
  return { w: 40, h: 82 };
}

/** A glass vial: colored flip-off cap, metal collar, shoulders; liquid filled to the level left. */
export function Vial({ color, fill, status, ml }: { color: string; fill: number; status: InventoryLot["status"]; ml: number | null }) {
  const { w, h } = vialDims(ml);
  const id = `vial${useId().replace(/:/g, "")}`;
  const glass = status === "expired" ? "#B4232F" : "#7C848C";
  const capH = Math.max(4, h * 0.12);
  const collarH = Math.max(3, h * 0.08);
  const neckW = w * 0.56;
  const nl = (w - neckW) / 2;
  const nr = nl + neckW;
  const y0 = capH + collarH; // top of the glass neck
  const s = y0 + h * 0.04; // neck to shoulder
  const y1 = s + h * 0.1; // shoulder to body
  const bottom = h - 1;
  const outline = `M${nl},${y0} L${nl},${s} Q1,${s} 1,${y1} L1,${bottom - 3} Q1,${bottom} 4,${bottom} L${w - 4},${bottom} Q${w - 1},${bottom} ${w - 1},${bottom - 3} L${w - 1},${y1} Q${w - 1},${s} ${nr},${s} L${nr},${y0} Z`;
  const level = Math.max(0, Math.min(1, fill)) * (bottom - y1);
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="shrink-0" aria-hidden>
      <defs>
        <clipPath id={id}>
          <path d={outline} />
        </clipPath>
      </defs>
      <path d={outline} fill="#F3F6F8" />
      <rect x="0" y={bottom - level} width={w} height={level} fill={color} opacity={status === "expired" ? 0.25 : 0.55} clipPath={`url(#${id})`} />
      {/* glass shine */}
      <rect x="3" y={y1 + 2} width={Math.max(1.5, w * 0.08)} height={Math.max(0, bottom - y1 - 6)} rx="1" fill="#FFFFFF" opacity="0.7" />
      <path d={outline} fill="none" stroke={glass} strokeWidth={status === "expired" ? 1.6 : 1} />
      <rect x={nl - 1} y={capH - 0.5} width={neckW + 2} height={collarH} rx="1" fill="#B9BEC3" stroke="#8E949A" strokeWidth="0.6" />
      <rect x={(w - neckW * 1.18) / 2} y="0.5" width={neckW * 1.18} height={capH} rx={Math.min(3, capH / 2)} fill={color} />
      {status === "expiring" && <circle cx={w - 4} cy={y1 + 4} r="3" fill="#D69E2E" stroke="#fff" strokeWidth="1" />}
    </svg>
  );
}

/** An IV bag (fluids). */
function Bag({ color, fill, status }: { color: string; fill: number; status: InventoryLot["status"] }) {
  const w = 18;
  const h = 60;
  const id = `bag${useId().replace(/:/g, "")}`;
  const level = Math.max(0, Math.min(1, fill)) * (h - 10);
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="shrink-0" aria-hidden>
      <defs>
        <clipPath id={id}>
          <rect x="1.5" y="8" width={w - 3} height={h - 10} rx="5" />
        </clipPath>
      </defs>
      <rect x={w / 2 - 2} y="1" width="4" height="7" rx="1" fill="#9AA0A6" />
      <rect x="1.5" y="8" width={w - 3} height={h - 10} rx="5" fill="#FFFFFF" />
      <rect x="0" y={h - 2 - level} width={w} height={level} fill={color} opacity={status === "expired" ? 0.35 : 0.6} clipPath={`url(#${id})`} />
      <rect x="1.5" y="8" width={w - 3} height={h - 10} rx="5" fill="none" stroke={status === "expired" ? "#B4232F" : "#7C848C"} strokeWidth="1" />
    </svg>
  );
}

function SupplyList({ items, onOpen }: { items: InventoryItem[]; onOpen: (id: string) => void }) {
  return (
    <Panel className="max-w-2xl overflow-x-auto">
      <table className="table-base">
        <tbody>
          {items.map((it) => (
            <tr key={it.formularyItemId} className="cursor-pointer hover:bg-mist/50" onClick={() => onOpen(it.formularyItemId)}>
              <td>
                <span className="mr-2 inline-block size-2.5 rounded-full align-middle" style={{ background: it.color }} aria-hidden />
                <span className="font-semibold">{it.name}</span>
                <ItemFlags item={it} />
              </td>
              <td className="text-right text-lg font-semibold tabular-nums">{fmtNum(it.usable)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}

// ── One item: lots and history

function ItemDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const detail = useQuery({ queryKey: ["inventory", id], queryFn: () => get<InventoryItemDetail>(`/api/inventory/items/${id}`) });
  const [action, setAction] = useState<{ kind: "remove" | "count" | "edit"; lot: InventoryLot } | null>(null);
  const [adding, setAdding] = useState(false);
  const d = detail.data;
  const refresh = async (next: InventoryItemDetail) => {
    qc.setQueryData(["inventory", id], next);
    await qc.invalidateQueries({ queryKey: ["inventory"], exact: true });
    setAction(null);
  };
  const each = d?.doseUnit === "each";
  return (
    <Dialog title={d?.name ?? "Inventory"} onClose={onClose} wide>
      {detail.error && <Alert>{errorMessage(detail.error)}</Alert>}
      {!d ? (
        <Loading />
      ) : (
        <div className="flex flex-col gap-5">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex h-24 items-end gap-1">
              <VialRow item={d} />
            </div>
            <div>
              <div className="text-xl font-semibold tabular-nums">{d.lots.length ? summary(d) : "None on hand"}</div>
              {strengthOf(d) && <div className="text-sm text-muted">{strengthOf(d)}</div>}
              <ItemFlags item={d} />
            </div>
          </div>
          <section>
            <div className="mb-2 flex items-center justify-between gap-2">
              <h3 className="font-semibold">Lots</h3>
              {can("inventory.manage") && (
                <Button variant="secondary" onClick={() => setAdding(true)}>
                  Add stock on hand
                </Button>
              )}
            </div>
            {d.lots.length === 0 ? (
              <p className="text-sm text-muted">Nothing on hand.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="table-base text-sm">
                  <thead>
                    <tr>
                      <th>Lot</th>
                      <th>BUD</th>
                      {!each && <th>Vial</th>}
                      <th className="text-right">On hand</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {d.lots.map((l) => (
                      <tr key={l.id}>
                        <td>
                          {l.lotNumber || <span className="text-muted">—</span>}
                          {l.opening && <div className="text-xs text-muted">Starting count: check and save (Edit)</div>}
                        </td>
                        <td className={l.status === "expired" ? "font-semibold text-danger" : l.status === "expiring" ? "font-semibold text-wait" : undefined}>
                          {l.expiresOn ? fmtDate(l.expiresOn) : <span className="text-muted">—</span>}
                          {l.status === "expired" && " (expired)"}
                        </td>
                        {!each && <td className="whitespace-nowrap">{vialLabel(d.doseUnit, l)}</td>}
                        <td className="text-right whitespace-nowrap tabular-nums">
                          <div className={l.remaining < 0 ? "font-semibold text-wait" : "font-semibold"}>{formatVials(l.remaining, l.perVial, d.doseUnit, d.kind)}</div>
                          {!each && <div className="text-xs text-muted">{formatDose(l.remaining, d.doseUnit)}</div>}
                        </td>
                        <td className="text-right whitespace-nowrap">
                          <Button variant="quiet" onClick={() => setAction({ kind: "remove", lot: l })}>
                            Waste
                          </Button>
                          {can("inventory.manage") && (
                            <>
                              <Button variant="quiet" onClick={() => setAction({ kind: "count", lot: l })}>
                                Count
                              </Button>
                              <Button variant="quiet" onClick={() => setAction({ kind: "edit", lot: l })}>
                                Edit
                              </Button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
          <section>
            <h3 className="mb-2 font-semibold">History</h3>
            <ul className="max-h-72 overflow-y-auto text-sm">
              {d.history.map((h) => (
                <li key={h.id} className="grid grid-cols-[7.5rem_1fr_auto] gap-x-3 border-b border-mist py-2 last:border-0">
                  <span className="text-muted">{new Date(h.at).toLocaleDateString(undefined, { month: "short", day: "numeric" })} {new Date(h.at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>
                  <span>
                    <span className="font-semibold">{CHANGE_REASON_LABELS[h.reason]}</span>
                    {h.lotNumber ? <span className="text-ink-soft"> · lot {h.lotNumber}</span> : null}
                    {h.note && <span className="text-ink-soft"> · {h.note}</span>}
                    {h.chartId && (
                      <>
                        {" · "}
                        <Link to={`/charts/${h.chartId}`} className="text-brand hover:underline">
                          chart
                        </Link>
                      </>
                    )}
                    <span className="text-muted"> · {h.by}</span>
                  </span>
                  <span className={`text-right tabular-nums ${h.lotId === null ? "text-muted line-through" : h.amount < 0 ? "text-ink" : "text-ok"}`}>
                    {h.amount > 0 ? "+" : "−"}
                    {formatDose(Math.abs(h.amount), d.doseUnit)}
                  </span>
                </li>
              ))}
              {!d.history.length && <li className="text-muted">No changes yet.</li>}
            </ul>
          </section>
        </div>
      )}
      {d && action?.kind === "remove" && <RemoveDialog item={d} lot={action.lot} onDone={refresh} onClose={() => setAction(null)} />}
      {d && action?.kind === "count" && <CountDialog item={d} lot={action.lot} onDone={refresh} onClose={() => setAction(null)} />}
      {d && action?.kind === "edit" && <LotDialog item={d} lot={action.lot} onDone={refresh} onClose={() => setAction(null)} />}
      {d && adding && (
        <StockDialog
          item={d}
          onDone={async (next) => {
            await refresh(next);
            setAdding(false);
          }}
          onClose={() => setAdding(false)}
        />
      )}
    </Dialog>
  );
}

function vialLabel(unit: DoseUnit, l: Pick<InventoryLot, "strengthPerMl" | "vialSizeMl">) {
  if (unit === "each") return "";
  const size = l.vialSizeMl ? `${fmtNum(l.vialSizeMl)} mL` : "size not set";
  return unit === "ml" || !l.strengthPerMl ? size : `${fmtNum(l.strengthPerMl, 4)} ${DOSE_UNIT_LABELS[unit]}/mL × ${size}`;
}

/** "N vials + M mL" (or a count for items counted each). */
function AmountFields({ unit, vials, ml, onVials, onMl, vialLabelText = "Vials" }: { unit: DoseUnit; vials: string; ml: string; onVials: (v: string) => void; onMl: (v: string) => void; vialLabelText?: string }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <Field label={unit === "each" ? "How many" : vialLabelText} type="number" min={0} step={unit === "each" ? 1 : "any"} value={vials} onChange={(e) => onVials(e.target.value)} />
      {unit !== "each" && <Field label="Plus mL" type="number" min={0} step="any" value={ml} onChange={(e) => onMl(e.target.value)} hint="From an open vial" />}
    </div>
  );
}

type WasteUnit = "vials" | "ml" | "dose";

function RemoveDialog({ item, lot, onDone, onClose }: { item: InventoryItemDetail; lot: InventoryLot; onDone: (d: InventoryItemDetail) => void; onClose: () => void }) {
  const [reason, setReason] = useState<"wasted" | "expired">(lot.status === "expired" ? "expired" : "wasted");
  const each = item.doseUnit === "each";
  const own = DOSE_UNIT_LABELS[item.doseUnit];
  const per = perVial(item.doseUnit, lot.strengthPerMl, lot.vialSizeMl);
  const perMl = item.doseUnit === "ml" ? 1 : lot.strengthPerMl;
  // Enter it in vials, mL, or the medication's own unit (mg, mcg, units…); only what this lot can convert.
  const units: { value: WasteUnit; label: string }[] = each
    ? []
    : [
        ...(per ? [{ value: "vials" as const, label: "Vials" }] : []),
        ...(perMl ? [{ value: "ml" as const, label: "mL" }] : []),
        ...(item.doseUnit !== "ml" ? [{ value: "dose" as const, label: own }] : []),
      ];
  const [unit, setUnit] = useState<WasteUnit>(lot.status === "expired" && per ? "vials" : (units.find((u) => u.value === "dose") ?? units[0])?.value ?? "dose");
  const [qty, setQty] = useState(lot.status === "expired" ? String(per ? Math.max(0, Math.round((lot.remaining / per) * 100) / 100) : Math.max(0, lot.remaining)) : "");
  const [note, setNote] = useState("");
  const n = Number(qty || 0);
  const body = each ? { vials: n, ml: 0, dose: 0 } : unit === "vials" ? { vials: n, ml: 0, dose: 0 } : unit === "ml" ? { vials: 0, ml: n, dose: 0 } : { vials: 0, ml: 0, dose: n };
  const amount = each ? n : unit === "dose" ? n : amountFor(item.doseUnit, lot.strengthPerMl, lot.vialSizeMl, body.vials, body.ml);
  const fmt = (x: number) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 3 }).format(x);
  // What it comes to, in each unit the lot can show.
  const same = !each && amount ? [formatDose(amount, item.doseUnit), ...(perMl && item.doseUnit !== "ml" ? [`${fmt(amount / perMl)} mL`] : []), ...(per ? [`${fmt(Math.round((amount / per) * 100) / 100)} vial${amount === per ? "" : "s"}`] : [])] : [];
  const save = useMutation({
    mutationFn: () => post<InventoryItemDetail>(`/api/inventory/lots/${lot.id}/remove`, { reason, ...body, note }),
    onSuccess: onDone,
  });
  return (
    <Dialog
      title={`Waste or expire: ${item.name}${lot.lotNumber ? `, lot ${lot.lotNumber}` : ""}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={save.isPending} disabled={!amount} onClick={() => save.mutate()}>
            Take off
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        <SelectField label="Why" value={reason} onChange={(e) => setReason(e.target.value as "wasted" | "expired")}>
          <option value="wasted">Wasted (dropped, contaminated, discarded after opening…)</option>
          <option value="expired">Expired</option>
        </SelectField>
        {each ? (
          <Field label="How many" type="number" min={0} step={1} value={qty} onChange={(e) => setQty(e.target.value)} />
        ) : (
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-3">
            <Field label="How much" type="number" min={0} step="any" value={qty} onChange={(e) => setQty(e.target.value)} />
            <SelectField label="In" value={unit} onChange={(e) => setUnit(e.target.value as WasteUnit)}>
              {units.map((u) => (
                <option key={u.value} value={u.value}>
                  {u.label}
                </option>
              ))}
            </SelectField>
          </div>
        )}
        {same.length > 0 && <p className="text-sm text-ink-soft">= {same.join(" · ")}</p>}
        {!each && (!per || !perMl) && <p className="text-sm text-muted">This lot has no {!lot.vialSizeMl ? "vial size" : "strength"} set, so some units can't be used. Fix the lot to use them.</p>}
        <TextAreaField label={reason === "wasted" ? "What happened" : "Note (optional)"} required={reason === "wasted"} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
    </Dialog>
  );
}

function CountDialog({ item, lot, onDone, onClose }: { item: InventoryItemDetail; lot: InventoryLot; onDone: (d: InventoryItemDetail) => void; onClose: () => void }) {
  const v = vialsOf(Math.max(0, lot.remaining), lot.perVial);
  const [vials, setVials] = useState(item.doseUnit === "each" ? String(Math.max(0, lot.remaining)) : String(v?.full ?? 0));
  const [ml, setMl] = useState(item.doseUnit === "each" || !v || !lot.vialSizeMl ? "" : String(Math.round(v.partial * lot.vialSizeMl * 10) / 10 || ""));
  const [note, setNote] = useState("");
  const amount = amountFor(item.doseUnit, lot.strengthPerMl, lot.vialSizeMl, Number(vials || 0), Number(ml || 0));
  const save = useMutation({
    mutationFn: () => post<InventoryItemDetail>(`/api/inventory/lots/${lot.id}/count`, { vials: Number(vials || 0), ml: Number(ml || 0), note }),
    onSuccess: onDone,
  });
  return (
    <Dialog
      title={`Count: ${item.name}${lot.lotNumber ? `, lot ${lot.lotNumber}` : ""}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={save.isPending} disabled={amount === null} onClick={() => save.mutate()}>
            Save count
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        <p className="text-sm text-ink-soft">
          What's actually on the shelf for this lot. On record: {formatVials(lot.remaining, lot.perVial, item.doseUnit, item.kind)}
          {item.doseUnit !== "each" && ` (${formatDose(lot.remaining, item.doseUnit)})`}.
        </p>
        <AmountFields unit={item.doseUnit} vials={vials} ml={ml} onVials={setVials} onMl={setMl} vialLabelText="Full vials" />
        {amount !== null && item.doseUnit !== "each" && <p className="text-sm text-ink-soft">= {formatDose(amount, item.doseUnit)}</p>}
        {amount === null && <Alert tone="info">Set this lot's strength and vial size first (Edit).</Alert>}
        <Field label="Note (optional)" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
    </Dialog>
  );
}

/** Stock on the shelf that isn't on record (the go-live count, or vials from a past purchase). */
function StockDialog({ item, onDone, onClose }: { item: InventoryItemDetail; onDone: (d: InventoryItemDetail) => void; onClose: () => void }) {
  const [v, setV] = useState({ purchaseLineId: null as string | null, lotNumber: "", expiresOn: "", strengthPerMl: item.strengthPerMl, vialSizeMl: item.vialSizeMl, vials: "", ml: "", note: "" });
  const amount = amountFor(item.doseUnit, v.strengthPerMl, v.vialSizeMl, Number(v.vials || 0), Number(v.ml || 0));
  const save = useMutation({
    mutationFn: () =>
      post<InventoryItemDetail>(`/api/inventory/items/${item.formularyItemId}/stock`, {
        purchaseLineId: v.purchaseLineId,
        lotNumber: v.lotNumber,
        expiresOn: v.expiresOn || null,
        strengthPerMl: v.strengthPerMl,
        vialSizeMl: v.vialSizeMl,
        vials: Number(v.vials || 0),
        ml: Number(v.ml || 0),
        note: v.note,
      }),
    onSuccess: onDone,
  });
  return (
    <Dialog
      title={`Add stock on hand: ${item.name}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={save.isPending} disabled={!amount} onClick={() => save.mutate()}>
            Add to the shelf
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        <p className="text-sm text-ink-soft">For vials on the shelf that aren't on record yet, like the go-live count. New deliveries go through Record purchase.</p>
        {item.purchases.length > 0 && (
          <SelectField
            label="From purchase (optional)"
            value={v.purchaseLineId ?? ""}
            onChange={(e) => {
              const p = item.purchases.find((x) => x.id === e.target.value);
              setV(
                p
                  ? { ...v, purchaseLineId: p.id, lotNumber: p.lotNumber, expiresOn: p.expiresOn ?? v.expiresOn, strengthPerMl: p.strengthPerMl ?? v.strengthPerMl, vialSizeMl: p.vialSizeMl ?? v.vialSizeMl }
                  : { ...v, purchaseLineId: null },
              );
            }}
          >
            <option value="">Not linked</option>
            {item.purchases.map((p) => (
              <option key={p.id} value={p.id}>
                {`${p.date ? fmtDate(p.date) : "No date"} · ${p.supplier}${p.invoiceNumber ? ` #${p.invoiceNumber}` : ""} · lot ${p.lotNumber || "—"}`}
              </option>
            ))}
          </SelectField>
        )}
        <AmountFields unit={item.doseUnit} vials={v.vials} ml={v.ml} onVials={(x) => setV({ ...v, vials: x })} onMl={(x) => setV({ ...v, ml: x })} vialLabelText="Full vials" />
        {amount !== null && amount > 0 && item.doseUnit !== "each" && <p className="text-sm text-ink-soft">= {formatDose(amount, item.doseUnit)}</p>}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Lot number" maxLength={60} value={v.lotNumber} onChange={(e) => setV({ ...v, lotNumber: e.target.value })} />
          <Field label="BUD / expiration" type="date" value={v.expiresOn} onChange={(e) => setV({ ...v, expiresOn: e.target.value })} />
        </div>
        {item.doseUnit !== "each" && (
          <div className="grid gap-4 sm:grid-cols-2">
            {needsStrength(item.doseUnit) ? (
              <Field
                label={`Strength (${DOSE_UNIT_LABELS[item.doseUnit]}/mL)`}
                type="number"
                min={0}
                step="any"
                value={v.strengthPerMl ?? ""}
                onChange={(e) => setV({ ...v, strengthPerMl: e.target.value === "" ? null : Number(e.target.value) })}
              />
            ) : (
              <div />
            )}
            <Field label="Vial size (mL)" type="number" min={0} step="any" value={v.vialSizeMl ?? ""} onChange={(e) => setV({ ...v, vialSizeMl: e.target.value === "" ? null : Number(e.target.value) })} />
          </div>
        )}
      </div>
    </Dialog>
  );
}

function LotDialog({ item, lot, onDone, onClose }: { item: InventoryItemDetail; lot: InventoryLot; onDone: (d: InventoryItemDetail) => void; onClose: () => void }) {
  const [v, setV] = useState({ lotNumber: lot.lotNumber, expiresOn: lot.expiresOn ?? "", strengthPerMl: lot.strengthPerMl ?? null, vialSizeMl: lot.vialSizeMl ?? null, purchaseLineId: lot.purchaseLineId });
  const canLink = lot.opening || !lot.purchaseLineId;
  const linked = item.purchases.find((p) => p.id === v.purchaseLineId);
  const purchaseLabel = (p: (typeof item.purchases)[number]) =>
    `${p.date ? fmtDate(p.date) : "No date"} · ${p.supplier}${p.invoiceNumber ? ` #${p.invoiceNumber}` : ""} · lot ${p.lotNumber || "—"}${p.expiresOn ? ` (BUD ${fmtDate(p.expiresOn)})` : ""}`;
  const save = useMutation({
    mutationFn: () => put<InventoryItemDetail>(`/api/inventory/lots/${lot.id}`, { ...v, expiresOn: v.expiresOn || null }),
    onSuccess: onDone,
  });
  return (
    <Dialog
      title={`Lot details: ${item.name}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        {canLink && item.purchases.length > 0 ? (
          <SelectField
            label="From purchase"
            hint="Pick the order these vials came from: the lot, BUD, and vial size fill in."
            value={v.purchaseLineId ?? ""}
            onChange={(e) => {
              const p = item.purchases.find((x) => x.id === e.target.value);
              setV(
                p
                  ? { ...v, purchaseLineId: p.id, lotNumber: p.lotNumber || v.lotNumber, expiresOn: p.expiresOn ?? v.expiresOn, strengthPerMl: p.strengthPerMl ?? v.strengthPerMl, vialSizeMl: p.vialSizeMl ?? v.vialSizeMl }
                  : { ...v, purchaseLineId: null },
              );
            }}
          >
            <option value="">Not linked</option>
            {item.purchases.map((p) => (
              <option key={p.id} value={p.id}>
                {purchaseLabel(p)}
              </option>
            ))}
          </SelectField>
        ) : (
          linked && <p className="text-sm text-ink-soft">From {purchaseLabel(linked)}</p>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Lot number" maxLength={60} value={v.lotNumber} onChange={(e) => setV({ ...v, lotNumber: e.target.value })} />
          <Field label="BUD / expiration" type="date" value={v.expiresOn} onChange={(e) => setV({ ...v, expiresOn: e.target.value })} />
        </div>
        {item.doseUnit !== "each" && (
          <div className="grid gap-4 sm:grid-cols-2">
            {needsStrength(item.doseUnit) ? (
              <Field
                label={`Strength (${DOSE_UNIT_LABELS[item.doseUnit]}/mL)`}
                type="number"
                min={0}
                step="any"
                value={v.strengthPerMl ?? ""}
                onChange={(e) => setV({ ...v, strengthPerMl: e.target.value === "" ? null : Number(e.target.value) })}
              />
            ) : (
              <div />
            )}
            <Field label="Vial size (mL)" type="number" min={0} step="any" value={v.vialSizeMl ?? ""} onChange={(e) => setV({ ...v, vialSizeMl: e.target.value === "" ? null : Number(e.target.value) })} />
          </div>
        )}
        {item.doseUnit !== "each" && <p className="text-sm text-muted">Changing the strength or vial size keeps the same number of vials on hand.</p>}
      </div>
    </Dialog>
  );
}

// ── Recording a purchase

interface Line {
  formularyItemId: string;
  newProduct: { name: string; drugName: string; kind: FormularyKind; doseUnit: DoseUnit; strengthPerMl: number | null } | null;
  /** Something not tracked in the formulary (e.g. sharps containers): a record only. */
  other: string | null;
  supplierProductId: string;
  qty: string;
  perBox: string;
  unitCents: number | null;
  lotNumber: string;
  expiresOn: string;
  strengthPerMl: number | null;
  vialSizeMl: number | null;
  /** Filled from an invoice: how the product was matched, and anything the AI wasn't sure of. */
  ai?: { match: InvoiceMatch; description: string; unsure: string | null; picked?: boolean };
}

const NEW = "__new";
const OTHER = "__other";
const NEW_SUPPLIER = "__supplier";
const blank = (): Line => ({ formularyItemId: "", newProduct: null, other: null, supplierProductId: "", qty: "", perBox: "", unitCents: null, lotNumber: "", expiresOn: "", strengthPerMl: null, vialSizeMl: null });

/** Record a purchase: the order (supplier, invoice, dates, cost) and its lines; formulary items go on the shelf unless it's history only. */
export function RecordPurchaseDialog({ kind, onClose }: { kind: FormularyKind; onClose: () => void }) {
  const qc = useQueryClient();
  const catalog = useQuery({ queryKey: ["catalog"], queryFn: () => get<CatalogResponse>("/api/catalog") });
  const suppliers = useQuery({ queryKey: ["suppliers"], queryFn: () => get<Supplier[]>("/api/suppliers") });
  const shelf = qc.getQueryData<InventoryShelf>(["inventory"]);
  const [head, setHead] = useState({
    supplierId: "",
    newSupplier: "",
    invoiceNumber: "",
    orderNumber: "",
    orderedOn: todayIn(),
    receivedOn: todayIn(),
    shippingCents: 0 as number | null,
    discountCents: null as number | null,
    taxCents: null as number | null,
    feesCents: null as number | null,
    totalCents: null as number | null,
    notes: "",
    addToStock: true,
  });
  const [lines, setLines] = useState<Line[]>([blank()]);
  const [attachments, setAttachments] = useState<PurchaseAttachment[]>([]);
  const [read, setRead] = useState<{ warnings: string[] } | null>(null);
  const formulary = (catalog.data?.formulary ?? []).filter((f) => f.active).sort((a, b) => a.name.localeCompare(b.name));
  const set = (i: number, patch: Partial<Line>) => setLines((cur) => cur.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const unitOf = (l: Line): DoseUnit | null => l.newProduct?.doseUnit ?? formulary.find((f) => f.id === l.formularyItemId)?.doseUnit ?? null;
  const vialsOfLine = (l: Line) => Number(l.qty || 0) * (l.perBox ? Number(l.perBox) : 1);
  const linesCents = lines.reduce((s, l) => s + (l.unitCents ?? 0) * vialsOfLine(l), 0);
  const expectedCents = linesCents + (head.shippingCents ?? 0) + (head.feesCents ?? 0) + (head.taxCents ?? 0) - (head.discountCents ?? 0);
  const allPriced = lines.every((l) => l.unitCents !== null);
  // Box prices split into per-vial cents round by up to half a cent a vial.
  const rounding = lines.reduce((s, l) => s + (l.perBox ? vialsOfLine(l) / 2 : 0), 0);
  const totalOff = head.totalCents !== null && allPriced && Math.abs(head.totalCents - expectedCents) > Math.max(5, lines.length * 2) + rounding;

  /** Put what the AI read into the form; staff check it and save as usual. */
  const applyDraft = (d: InvoiceDraft) => {
    setHead((h) => ({
      ...h,
      supplierId: d.supplierId ?? (d.supplierName ? NEW_SUPPLIER : h.supplierId),
      newSupplier: d.supplierId ? h.newSupplier : d.supplierName,
      invoiceNumber: d.invoiceNumber || h.invoiceNumber,
      orderNumber: d.orderNumber || h.orderNumber,
      orderedOn: d.orderedOn ?? h.orderedOn,
      shippingCents: d.shippingCents,
      discountCents: d.discountCents || null,
      taxCents: d.taxCents || null,
      feesCents: d.feesCents || null,
      totalCents: d.totalCents,
      notes: [h.notes, d.notes].filter(Boolean).join(" · ").slice(0, 1000),
    }));
    if (d.lines.length) {
      setLines(
        d.lines.map((x): Line => {
          const f = x.formularyItemId ? formulary.find((y) => y.id === x.formularyItemId) : undefined;
          const s = x.formularyItemId ? shelf?.items.find((y) => y.formularyItemId === x.formularyItemId) : undefined;
          return {
            formularyItemId: x.match === "history" || x.match === "suggested" ? (x.formularyItemId ?? "") : "",
            newProduct: x.match === "new" ? x.newProduct : null,
            other: x.match === "other" ? x.description : null,
            supplierProductId: x.supplierProductId,
            qty: x.quantity ? String(x.quantity) : "",
            perBox: x.perBox ? String(x.perBox) : "",
            unitCents: x.unitCents,
            lotNumber: x.lotNumber,
            expiresOn: x.expiresOn ?? "",
            strengthPerMl: x.strengthPerMl ?? s?.strengthPerMl ?? f?.strengthPerMl ?? null,
            vialSizeMl: x.vialSizeMl ?? s?.vialSizeMl ?? f?.vialSizeMl ?? null,
            ai: { match: x.match, description: x.description, unsure: x.unsure },
          };
        }),
      );
    }
    setAttachments((cur) => [...cur, ...d.attachments].slice(0, 10));
    setRead({ warnings: d.warnings });
  };
  const save = useMutation({
    mutationFn: async () => {
      let supplierId = head.supplierId;
      if (supplierId === NEW_SUPPLIER) supplierId = (await post<Supplier>("/api/suppliers", { name: head.newSupplier })).id;
      return post("/api/purchases", {
        supplierId,
        invoiceNumber: head.invoiceNumber,
        orderNumber: head.orderNumber,
        orderedOn: head.orderedOn || null,
        receivedOn: head.receivedOn || null,
        shippingCents: head.shippingCents ?? 0,
        discountCents: head.discountCents ?? 0,
        taxCents: head.taxCents ?? 0,
        feesCents: head.feesCents ?? 0,
        totalCents: head.totalCents,
        notes: head.notes,
        addToStock: head.addToStock,
        attachments,
        lines: lines.map((l) => ({
          formularyItemId: l.newProduct || l.other !== null ? null : l.formularyItemId,
          newProduct: l.newProduct,
          productName: l.other ?? "",
          supplierProductId: l.supplierProductId,
          lotNumber: l.lotNumber,
          expiresOn: l.expiresOn || null,
          strengthPerMl: l.newProduct ? l.newProduct.strengthPerMl : l.strengthPerMl,
          vialSizeMl: l.vialSizeMl,
          quantity: vialsOfLine(l),
          unitCents: l.unitCents,
        })),
      });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["inventory"] });
      await qc.invalidateQueries({ queryKey: ["catalog"] });
      await qc.invalidateQueries({ queryKey: ["purchases"] });
      await qc.invalidateQueries({ queryKey: ["suppliers"] });
      onClose();
    },
  });
  const ready =
    (head.supplierId && (head.supplierId !== NEW_SUPPLIER || head.newSupplier.trim())) &&
    lines.every((l) => (l.formularyItemId || l.newProduct?.name || l.other?.trim()) && vialsOfLine(l) > 0 && (!head.addToStock || l.other !== null || Number.isInteger(vialsOfLine(l))));

  return (
    <Dialog
      title="Record purchase"
      onClose={onClose}
      wide
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={save.isPending} disabled={!ready} onClick={() => save.mutate()}>
            {head.addToStock ? "Save and add to inventory" : "Save (history only)"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        <InvoiceUpload onDraft={applyDraft} />
        {read && (
          <div role="status" className={cx("rounded-[var(--radius-control)] px-3.5 py-2.5 text-sm", read.warnings.length ? "bg-wait-wash text-wait" : "bg-ok-wash text-ok")}>
            {read.warnings.length ? (
              <>
                <p className="font-semibold">Filled in from the invoice. Check these before saving:</p>
                <ul className="mt-1 list-disc pl-5">
                  {read.warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              </>
            ) : (
              "Filled in from the invoice. Check it against the invoice before saving."
            )}
          </div>
        )}
        {attachments.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-ink-soft">Saved with this purchase:</span>
            {attachments.map((a, i) => (
              <span key={a.key} className="inline-flex items-center gap-1 rounded-full bg-mist px-2.5 py-0.5">
                {a.name}
                <button type="button" className="text-muted hover:text-danger" aria-label={`Don't save ${a.name}`} onClick={() => setAttachments((cur) => cur.filter((_, j) => j !== i))}>
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-3">
          <SelectField label="Supplier" value={head.supplierId} onChange={(e) => setHead({ ...head, supplierId: e.target.value })}>
            <option value="">Pick…</option>
            {(suppliers.data ?? [])
              .filter((x) => x.active)
              .map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            <option value={NEW_SUPPLIER}>New supplier…</option>
          </SelectField>
          <Field label="Invoice #" maxLength={60} value={head.invoiceNumber} onChange={(e) => setHead({ ...head, invoiceNumber: e.target.value })} />
          <Field label="Order # (optional)" maxLength={60} value={head.orderNumber} onChange={(e) => setHead({ ...head, orderNumber: e.target.value })} />
          {head.supplierId === NEW_SUPPLIER && (
            <Field label="New supplier's name" required maxLength={120} className="sm:col-span-3" value={head.newSupplier} onChange={(e) => setHead({ ...head, newSupplier: e.target.value })} />
          )}
          <Field label="Order date" type="date" value={head.orderedOn} onChange={(e) => setHead({ ...head, orderedOn: e.target.value })} />
          <Field label="Received" type="date" value={head.receivedOn} onChange={(e) => setHead({ ...head, receivedOn: e.target.value })} />
          <MoneyField label="Shipping" optional cents={head.shippingCents} onChange={(c) => setHead({ ...head, shippingCents: c })} />
        </div>
        <Checkbox
          label="Add these to inventory"
          description="Off for an older order you're only recording (its vials are already counted, or used up)."
          checked={head.addToStock}
          onChange={(on) => setHead({ ...head, addToStock: on })}
        />
        <p className="text-sm text-ink-soft">One line per product and lot, as on the invoice.</p>
        {lines.map((l, i) => {
          const unit = unitOf(l);
          const strength = l.newProduct ? l.newProduct.strengthPerMl : l.strengthPerMl;
          const each = unit ? perVial(unit, strength, l.vialSizeMl) : null;
          const n = vialsOfLine(l);
          return (
            <Panel key={i} className="flex flex-col gap-3 p-4">
              {l.ai && (
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-ink-soft">Invoice: {l.ai.description}</span>
                  {l.ai.match === "suggested" && !l.ai.picked && <Badge tone="wait">Check match</Badge>}
                  {l.ai.match === "new" && !l.ai.picked && <Badge tone="brand">New product</Badge>}
                  {l.ai.match === "history" && !l.ai.picked && <Badge tone="ok">Same item # as before</Badge>}
                  {l.ai.unsure && <span className="text-wait">{l.ai.unsure}</span>}
                </div>
              )}
              <div className="grid items-end gap-3 sm:grid-cols-[1fr_10rem_auto]">
                <SelectField
                  label="Product"
                  value={l.newProduct ? NEW : l.other !== null ? OTHER : l.formularyItemId}
                  onChange={(e) => {
                    // Picking a product by hand confirms the match.
                    if (l.ai) set(i, { ai: { ...l.ai, picked: true } });
                    if (e.target.value === NEW) {
                      set(i, { formularyItemId: "", other: null, newProduct: { name: "", drugName: "", kind, doseUnit: kind === "medication" ? "mg" : "each", strengthPerMl: null } });
                      return;
                    }
                    if (e.target.value === OTHER) {
                      set(i, { formularyItemId: "", newProduct: null, other: "" });
                      return;
                    }
                    const f = formulary.find((x) => x.id === e.target.value);
                    const s = shelf?.items.find((x) => x.formularyItemId === e.target.value);
                    set(i, { formularyItemId: e.target.value, newProduct: null, other: null, strengthPerMl: s?.strengthPerMl ?? f?.strengthPerMl ?? null, vialSizeMl: s?.vialSizeMl ?? f?.vialSizeMl ?? null });
                  }}
                >
                  <option value="">Pick…</option>
                  {(["medication", "fluid", "supply"] as const).map((k) => (
                    <optgroup key={k} label={k === "medication" ? "Medications" : k === "fluid" ? "Fluids" : "Supplies"}>
                      {formulary
                        .filter((f) => f.kind === k)
                        .map((f) => (
                          <option key={f.id} value={f.id}>
                            {f.name}
                          </option>
                        ))}
                    </optgroup>
                  ))}
                  <option value={NEW}>New product (add to formulary)…</option>
                  <option value={OTHER}>Other (not tracked in inventory)…</option>
                </SelectField>
                <Field label="Supplier's item #" maxLength={60} value={l.supplierProductId} onChange={(e) => set(i, { supplierProductId: e.target.value })} hint="Prod ID, item #, or NDC" />
                {lines.length > 1 && (
                  <Button variant="quiet" onClick={() => setLines((cur) => cur.filter((_, j) => j !== i))}>
                    Remove
                  </Button>
                )}
              </div>
              {l.other !== null && <Field label="What it is" required maxLength={200} value={l.other} onChange={(e) => set(i, { other: e.target.value })} />}
              {l.newProduct && (
                <div className="grid gap-3 rounded-[var(--radius-control)] bg-mist/60 p-3 sm:grid-cols-2">
                  <Field label="Name" required maxLength={80} value={l.newProduct.name} onChange={(e) => set(i, { newProduct: { ...l.newProduct!, name: e.target.value } })} />
                  <Field label="Drug name or contents" maxLength={160} value={l.newProduct.drugName} onChange={(e) => set(i, { newProduct: { ...l.newProduct!, drugName: e.target.value } })} />
                  <SelectField
                    label="Kind"
                    value={l.newProduct.kind}
                    onChange={(e) => {
                      const k = e.target.value as FormularyKind;
                      set(i, { newProduct: { ...l.newProduct!, kind: k, doseUnit: k === "medication" ? l.newProduct!.doseUnit : "each" } });
                    }}
                  >
                    <option value="medication">Medication</option>
                    <option value="fluid">Fluid</option>
                    <option value="supply">Supply</option>
                  </SelectField>
                  <SelectField label="Measured in" value={l.newProduct.doseUnit} onChange={(e) => set(i, { newProduct: { ...l.newProduct!, doseUnit: e.target.value as DoseUnit } })}>
                    {DOSE_UNITS.map((u) => (
                      <option key={u} value={u}>
                        {u === "each" ? "Each (count)" : u === "ml" ? "mL (compounded or premixed)" : DOSE_UNIT_LABELS[u]}
                      </option>
                    ))}
                  </SelectField>
                </div>
              )}
              {(unit || l.other !== null) && (
                <>
                  <div className="grid gap-3 sm:grid-cols-5">
                    {unit && needsStrength(unit) && (
                      <Field
                        label={`Strength (${DOSE_UNIT_LABELS[unit]}/mL)`}
                        type="number"
                        min={0}
                        step="any"
                        value={strength ?? ""}
                        onChange={(e) => {
                          const s = e.target.value === "" ? null : Number(e.target.value);
                          if (l.newProduct) set(i, { newProduct: { ...l.newProduct, strengthPerMl: s } });
                          else set(i, { strengthPerMl: s });
                        }}
                      />
                    )}
                    {unit && unit !== "each" && (
                      <Field label="Vial size (mL)" type="number" min={0} step="any" value={l.vialSizeMl ?? ""} onChange={(e) => set(i, { vialSizeMl: e.target.value === "" ? null : Number(e.target.value) })} />
                    )}
                    <Field label="How many" type="number" min={1} step={1} value={l.qty} onChange={(e) => set(i, { qty: e.target.value })} />
                    <Field label={unit === "each" || !unit ? "Per box (optional)" : "Vials per box (optional)"} type="number" min={1} step={1} value={l.perBox} onChange={(e) => set(i, { perBox: e.target.value })} hint={l.perBox ? "How many = boxes" : undefined} />
                    <MoneyField label={unit === "each" || !unit ? "Price each" : "Price per vial"} optional cents={l.unitCents} onChange={(c) => set(i, { unitCents: c })} />
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Lot number" maxLength={60} value={l.lotNumber} onChange={(e) => set(i, { lotNumber: e.target.value })} />
                    <Field label="BUD / expiration" type="date" value={l.expiresOn} onChange={(e) => set(i, { expiresOn: e.target.value })} />
                  </div>
                  {n > 0 && (
                    <p className="text-sm text-ink-soft">
                      = {fmtNum(n)} {unit === "each" || !unit ? "" : n === 1 ? "vial" : "vials"}
                      {unit && unit !== "each" && (each ? ` · ${formatDose(each * n, unit)}` : " · enter the strength and vial size")}
                      {l.unitCents !== null && ` · ${money(l.unitCents * n)}`}
                    </p>
                  )}
                </>
              )}
            </Panel>
          );
        })}
        <div>
          <Button variant="secondary" onClick={() => setLines((cur) => [...cur, blank()])}>
            Add another line
          </Button>
        </div>
        <div className="grid items-start gap-3 sm:grid-cols-4">
          <MoneyField label="Discount" optional cents={head.discountCents} onChange={(c) => setHead({ ...head, discountCents: c })} hint="Promo or credit" />
          <MoneyField label="Fees" optional cents={head.feesCents} onChange={(c) => setHead({ ...head, feesCents: c })} hint="Cold pack, handling…" />
          <MoneyField label="Tax" optional cents={head.taxCents} onChange={(c) => setHead({ ...head, taxCents: c })} />
          <MoneyField label="Total charged" optional cents={head.totalCents} onChange={(c) => setHead({ ...head, totalCents: c })} hint="From the invoice" />
        </div>
        <p className={cx("text-sm", totalOff ? "font-medium text-wait" : "text-ink-soft")}>
          Lines {money(linesCents)} + shipping {money(head.shippingCents ?? 0)}
          {head.feesCents ? ` + fees ${money(head.feesCents)}` : ""}
          {head.taxCents ? ` + tax ${money(head.taxCents)}` : ""}
          {head.discountCents ? ` − discount ${money(head.discountCents)}` : ""} = {money(expectedCents)}
          {totalOff && `, but the total charged is ${money(head.totalCents!)}. Check the prices; otherwise the difference is spread over the lines as extra cost.`}
        </p>
        <Field label="Notes (optional)" maxLength={1000} value={head.notes} onChange={(e) => setHead({ ...head, notes: e.target.value })} placeholder="Back-order, substitutions…" />
      </div>
    </Dialog>
  );
}
